import { SUPABASE_URL } from '../utils/supabase/url';
/**
 * Gmail Service — frontend wrapper around the per-user Gmail proxy
 * (src/supabase/functions/server/routes/gmail_proxy_routes.tsx). All calls go
 * through the Edge Function so the user's Google access token never
 * touches the browser.
 */

import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b/gmail`;

export type {
  GmailAttachment, GmailHeader, GmailLabel, GmailListResponse, GmailMessage, GmailPayloadPart, GmailProfile, GmailThread, InlineImage,
} from './gmailMessage';
export { extractBody, getHeader, isUnread, listAttachments, listInlineImages, parseAddress } from './gmailMessage';
import type { GmailLabel, GmailListResponse, GmailMessage, GmailProfile, GmailThread } from './gmailMessage';

async function get<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders();
  const r = await fetch(`${API_BASE}${path}`, { headers });
  const data = await r.json();
  if (!r.ok || data.success === false) {
    throw new Error(data.error || `Gmail request failed (${r.status})`);
  }
  return data;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const r = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body ?? {}),
  });
  const data = await r.json();
  if (!r.ok || data.success === false) {
    throw new Error(data.error || `Gmail request failed (${r.status})`);
  }
  return data;
}

export const GmailService = {
  listMessages(opts: {
    label?: string;
    q?: string;
    pageToken?: string;
    maxResults?: number;
  } = {}): Promise<GmailListResponse & { success: true }> {
    const params = new URLSearchParams();
    if (opts.label) params.set('label', opts.label);
    if (opts.q) params.set('q', opts.q);
    if (opts.pageToken) params.set('pageToken', opts.pageToken);
    if (opts.maxResults) params.set('maxResults', String(opts.maxResults));
    return get(`/messages?${params}`);
  },

  batchMetadata(ids: string[]): Promise<{ success: true; messages: GmailMessage[] }> {
    return post('/messages/batchMetadata', { ids });
  },

  getMessage(id: string): Promise<{ success: true; message: GmailMessage }> {
    return get(`/messages/${encodeURIComponent(id)}?format=full`);
  },

  getThread(id: string): Promise<{ success: true; thread: GmailThread }> {
    return get(`/threads/${encodeURIComponent(id)}`);
  },

  modify(
    id: string,
    addLabelIds: string[] = [],
    removeLabelIds: string[] = [],
  ): Promise<{ success: true; notificationSyncFailed?: boolean }> {
    return post(`/messages/${encodeURIComponent(id)}/modify`, { addLabelIds, removeLabelIds });
  },

  async markRead(id: string): Promise<void> {
    const result = await this.modify(id, [], ['UNREAD']);
    if (!result.notificationSyncFailed) {
      window.dispatchEvent(new CustomEvent('gmail:message-read', { detail: { messageId: id } }));
    }
  },

  /** One-shot backlog reconcile: clear notifications for Gmail messages that
   *  are no longer unread. Safe to call on load; returns how many were cleared. */
  reconcileNotificationReads(): Promise<{ success: true; reconciled: number; skipped?: string }> {
    return post('/reconcile-notification-reads', {});
  },

  markUnread(id: string) {
    return this.modify(id, ['UNREAD'], []);
  },

  archive(id: string) {
    return this.modify(id, [], ['INBOX']);
  },

  trash(id: string) {
    return post(`/messages/${encodeURIComponent(id)}/trash`, {});
  },

  send(payload: {
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    bodyText?: string;
    bodyHtml?: string;
    threadId?: string;
    inReplyTo?: string;
    references?: string;
    attachments?: Array<{ filename: string; mimeType: string; dataBase64: string }>;
  }) {
    return post<{ success: true; message: GmailMessage }>('/messages/send', payload);
  },

  attachmentUrl(msgId: string, attId: string, filename: string, mimeType: string): string {
    const params = new URLSearchParams({ filename, mimeType });
    return `${API_BASE}/attachments/${encodeURIComponent(msgId)}/${encodeURIComponent(attId)}?${params}`;
  },

  /**
   * Returns a Blob for the attachment so the caller can decide what to do
   * (download, preview, attach to a project, etc.). We can't put the auth
   * header on a plain <a href>, so this fetches with auth and the caller
   * uses URL.createObjectURL on the result.
   */
  async fetchAttachment(
    msgId: string,
    attId: string,
    filename: string,
    mimeType: string,
  ): Promise<Blob> {
    const headers = await getAuthHeaders();
    const r = await fetch(this.attachmentUrl(msgId, attId, filename, mimeType), { headers });
    if (!r.ok) throw new Error(`Attachment fetch failed (${r.status})`);
    return await r.blob();
  },

  labels(): Promise<{ success: true; labels: GmailLabel[] }> {
    return get('/labels');
  },

  // The /labels list omits counts; the per-label detail includes
  // messagesTotal / messagesUnread (e.g. label('INBOX') for the unread badge).
  label(id: string): Promise<{ success: true; label: GmailLabel }> {
    return get(`/labels/${encodeURIComponent(id)}`);
  },

  profile(): Promise<{ success: true; profile: GmailProfile }> {
    return get('/profile');
  },

  startWatch(): Promise<{ success: true; historyId?: string; expiration?: string; skipped?: 'already_watching' | 'pubsub_not_configured' }> {
    return post('/watch', {});
  },

  stopWatch(): Promise<{ success: true }> {
    return post('/watch/stop', {});
  },
};
